import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  getAllVideoGallerys,
  getVideoGalleryByID,
  updateVideoGallery,
  createVideoGallery,
} from "./services/videogallery.services";
import { clearVideoGalleryDetails } from "./videoGallerySlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";

const CreateVideoGallery: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const VideoGalleryID = useAppSelector((state) => state.drawer.data);
  const VideoGalleryDetail = useAppSelector(
    (state) => state.videoGallery.VideoGalleryDetail,
  );
  const loading = useAppSelector((state) => state.videoGallery.formLoading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (VideoGalleryID) {
      dispatch(getVideoGalleryByID(VideoGalleryID));
    }
    return () => {
      dispatch(clearVideoGalleryDetails());
    };
  }, [VideoGalleryID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [VideoGalleryDetail]);

  const handleSubmit = async (values: any) => {
    const payload = {
      Title: values?.Title ? values?.Title : "",
      VideoURL: values?.VideoURL ? values?.VideoURL : "",
      IsActive: values?.IsActive === 1,
    };
    try {
      if (VideoGalleryID) {
        const response = await dispatch(
          updateVideoGallery({ ...payload, ID: VideoGalleryID }),
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllVideoGallerys());
          dispatch(clearVideoGalleryDetails());
        }
      } else {
        const response = await dispatch(createVideoGallery(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllVideoGallerys());
          dispatch(clearVideoGalleryDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={VideoGalleryDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 0, cols: 0 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {VideoGalleryID
                      ? "Update Video Gallery"
                      : "Create Video Gallery"}
                  </Typography.h4>
                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>


                <GridLayoutItem>
                  <Field
                    name="VideoURL"
                    label="Video URL"
                    placeholder="Video URL"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={VideoGalleryID ? "Update" : "Create"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateVideoGallery;
